import { Navigate, useParams } from 'react-router-dom';
import useSWR from 'swr';
import { parseAsStringLiteral, useQueryState } from 'nuqs';
import axiosInstance from '@/lib/axios';
import { Calendar, Loader2 } from 'lucide-react';
import {
  SegmentedTabs,
  SegmentedTabsContent,
  SegmentedTabsList,
  SegmentedTabsTrigger,
} from '@/components/ui/segmented-tabs';
import { EventCard, type Event } from '@/components/events/EventCard';
import { COMMUNITY_EVENTS_LANDING_EVENT } from '@/lib/community-navigation';
import { EventsPageShell } from './components/EventsPageShell';

interface EventsResponse {
  upcomingEvents: Event[];
  pastEvents: Event[];
}

const EVENT_TABS = ['upcoming', 'past'] as const;
type EventTab = (typeof EVENT_TABS)[number];

const fetcher = (url: string) => axiosInstance.get(url).then((res) => res.data);

export default function EventsPage() {
  const { communityId } = useParams<{ communityId: string }>();
  const landingEvent = communityId ? COMMUNITY_EVENTS_LANDING_EVENT[communityId] : undefined;
  const [tabParam, setTabParam] = useQueryState(
    'tab',
    parseAsStringLiteral(EVENT_TABS).withDefault('upcoming').withOptions({
      history: 'replace',
      clearOnDefault: true,
    })
  );
  const activeTab: EventTab = tabParam;

  const { data, error, isLoading } = useSWR<EventsResponse>(
    communityId && !landingEvent ? `/api/communities/${communityId}/events` : null,
    fetcher
  );

  if (communityId && landingEvent) {
    return <Navigate to={`/${communityId}/events/${landingEvent}`} replace />;
  }

  if (isLoading) {
    return (
      <div className="flex h-[50vh] w-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return <div className="text-center text-red-500 py-10">Failed to load events</div>;
  }

  return (
    <EventsPageShell>
      <div className="space-y-6 md:space-y-8 pb-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Events</h1>
            <p className="text-muted-foreground mt-2">
              Discover and join upcoming events in the community.
            </p>
          </div>
        </div>

        <SegmentedTabs
          value={activeTab}
          onValueChange={(value) => {
            void setTabParam(value as EventTab);
          }}
          className="w-full"
        >
          <SegmentedTabsList className="mb-8 w-full sm:w-fit">
            <SegmentedTabsTrigger value="upcoming">Upcoming</SegmentedTabsTrigger>
            <SegmentedTabsTrigger value="past">Past</SegmentedTabsTrigger>
          </SegmentedTabsList>

          <SegmentedTabsContent value="upcoming">
            {data?.upcomingEvents.length === 0 ? (
              <EmptyState message="No upcoming events found" />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {data?.upcomingEvents.map((event) => (
                  <EventCard key={event.id} event={event} communityId={communityId!} />
                ))}
              </div>
            )}
          </SegmentedTabsContent>

          <SegmentedTabsContent value="past">
            {data?.pastEvents.length === 0 ? (
              <EmptyState message="No past events found" />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {data?.pastEvents.map((event) => (
                  <EventCard key={event.id} event={event} communityId={communityId!} />
                ))}
              </div>
            )}
          </SegmentedTabsContent>
        </SegmentedTabs>
      </div>
    </EventsPageShell>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="text-center py-20 bg-muted/30 rounded-3xl border border-dashed border-border">
      <Calendar className="h-12 w-12 mx-auto text-muted-foreground opacity-50 mb-4" />
      <h3 className="text-xl font-medium">{message}</h3>
      <p className="text-muted-foreground">Stay tuned for updates!</p>
    </div>
  );
}
